import { Link } from "react-router-dom";
import { ArrowLeft, Building2, CheckCircle2, HardHat, Wrench, ShieldCheck, ClipboardList, Hammer, Cog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import logo from "@/assets/arabian-alwaha-logo.jpg";
import heroImg from "@/assets/hero-oil-gas.jpg";

const Contracting = () => {
  const { language } = useLanguage();
  const isArabic = language === "ar";

  const t = {
    back: isArabic ? "العودة إلى الموقع" : "Back to website",
    eyebrow: isArabic ? "خدماتنا · المقاولات" : "Our services · Contracting",
    title: isArabic ? "المقاولات العامة والصناعية" : "General & Industrial Contracting",
    lede: isArabic
      ? "تنفيذ مشاريع متكاملة في قطاعات النفط والغاز والصناعة، من التخطيط إلى التشغيل، بأعلى معايير السلامة والجودة."
      : "End-to-end project execution across oil, gas and industrial sectors — from planning through commissioning, to the highest safety and quality standards.",
    overviewTitle: isArabic ? "نبذة عن خدمة المقاولات" : "About our contracting practice",
    overviewBody: isArabic
      ? "تجمع العربية الواحة بين عقود من الخبرة الميدانية في الجبيل الصناعية وقدرة تنفيذية موثوقة. نعمل مع كبرى شركات EPC وملاك المشاريع، ونلتزم بمواعيد التسليم وشروط السلامة والجودة دون تنازل."
      : "Arabian Alwaha combines decades of on-the-ground expertise in Jubail Industrial City with proven delivery capability. We work alongside leading EPC contractors and project owners, honoring schedules, safety and quality without compromise.",
    capabilitiesTitle: isArabic ? "إمكاناتنا الرئيسية" : "Key capabilities",
    capabilities: [
      { icon: ClipboardList, title: isArabic ? "إدارة المشاريع" : "Project management", body: isArabic ? "تخطيط شامل وإدارة الجدول الزمني والميزانية حتى التسليم." : "Full planning, schedule and budget management through handover." },
      { icon: Hammer, title: isArabic ? "أعمال البناء" : "Civil & construction works", body: isArabic ? "أعمال مدنية وبنية تحتية بمعايير صناعية عالية." : "Civil works and infrastructure to demanding industrial standards." },
      { icon: Cog, title: isArabic ? "التركيب الميكانيكي" : "Mechanical installation", body: isArabic ? "تركيب الأنابيب والمعدات الدوارة وأنظمة العمليات." : "Piping, rotating equipment and process system installation." },
      { icon: Wrench, title: isArabic ? "الصيانة والإيقاف" : "Maintenance & shutdowns", body: isArabic ? "صيانة دورية ودعم خلال إيقافات التشغيل المخطط لها." : "Routine maintenance and dedicated support during planned shutdowns." },
      { icon: ShieldCheck, title: isArabic ? "السلامة والامتثال" : "HSE & compliance", body: isArabic ? "ثقافة السلامة أولاً والتزام كامل بمتطلبات العميل والجهات الرقابية." : "Safety-first culture with full compliance to client and regulatory requirements." },
      { icon: HardHat, title: isArabic ? "تعبئة القوى العاملة" : "Workforce mobilization", body: isArabic ? "فرق فنية مؤهلة جاهزة للنشر السريع في الموقع." : "Qualified technical crews ready for rapid site mobilization." },
    ],
    projectsTitle: isArabic ? "نماذج من سجل أعمالنا" : "Selected projects",
    projectsLede: isArabic
      ? "مشاريع نفذناها مع شركاء عالميين في أكثر بيئات العمل تطلباً في المملكة."
      : "Projects delivered with global partners in some of the Kingdom's most demanding environments.",
    projects: [
      { name: "SATORP Refinery", client: "Seapam", period: "2010 – 2013", type: isArabic ? "البنية التحتية للنفط والغاز" : "Oil & Gas infrastructure" },
      { name: "Dammam MENA Port", client: "Saipem", period: "2013 – 2014", type: isArabic ? "تطوير الموانئ" : "Port development" },
      { name: "Manifa Field", client: "Discovery", period: "2012 – 2015", type: isArabic ? "تطوير حقل بحري" : "Offshore field development" },
      { name: "Ma'aden Phosphate", client: "Samsung", period: "2011 – 2014", type: isArabic ? "معالجة المعادن" : "Mining & minerals processing" },
      { name: "SATORP I&C", client: "Carlo Gavazzi", period: "2010 – 2013", type: isArabic ? "أنظمة التحكم والقياس" : "Instrumentation & control" },
      { name: isArabic ? "خدمات تشغيل وصيانة" : "Industrial Support", client: "JGC / KBR", period: isArabic ? "٢٠١٤ – الحاضر" : "2014 – Present", type: isArabic ? "تشغيل وصيانة" : "Operations & maintenance" },
    ],
    ctaTitle: isArabic ? "هل لديك مشروع قادم؟" : "Have a project coming up?",
    ctaBody: isArabic ? "تحدث مع فريق المقاولات لدينا حول متطلباتك." : "Talk to our contracting team about your scope.",
    ctaPrimary: isArabic ? "اطلب عرض سعر" : "Request a quote",
    ctaSecondary: isArabic ? "تصفح كل الخدمات" : "Browse all services",
  };

  return (
    <div className="min-h-screen bg-background text-foreground" dir={isArabic ? "rtl" : "ltr"}>
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/80 border-b border-border/40">
        <div className="container mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-sm font-medium text-foreground/80 hover:text-primary transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t.back}
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroImg})` }} aria-hidden="true" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, hsl(160 65% 12% / 0.96) 0%, hsl(160 55% 22% / 0.92) 55%, hsl(45 80% 30% / 0.85) 100%)" }} aria-hidden="true" />
        <div className="relative container mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="max-w-4xl">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-xl bg-white/95 p-1.5 shadow-elegant">
                <img src={logo} alt="Arabian Alwaha" className="w-full h-full object-contain" />
              </div>
              <p className="text-secondary text-xs uppercase tracking-[0.3em] font-bold">{t.eyebrow}</p>
            </div>
            <h1 className="text-white font-bold tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-6">{t.title}</h1>
            <p className="text-white/85 text-lg lg:text-xl max-w-2xl leading-relaxed">{t.lede}</p>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="py-20 lg:py-24">
        <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
          <p className="text-primary text-xs uppercase tracking-[0.3em] font-bold mb-4">{isArabic ? "نبذة" : "Overview"}</p>
          <h2 className="text-3xl lg:text-4xl font-bold text-accent mb-6">{t.overviewTitle}</h2>
          <p className="text-lg text-muted-foreground leading-relaxed">{t.overviewBody}</p>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="py-20 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="text-primary text-xs uppercase tracking-[0.3em] font-bold mb-4">{isArabic ? "الإمكانات" : "Capabilities"}</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-accent">{t.capabilitiesTitle}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.capabilities.map((c) => (
              <div key={c.title} className="bg-card rounded-xl p-6 shadow-soft hover:shadow-elegant transition-elegant border border-border/40">
                <div className="w-12 h-12 rounded-lg gradient-primary flex items-center justify-center mb-4">
                  <c.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-accent mb-2">{c.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="py-20 lg:py-24">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <p className="text-primary text-xs uppercase tracking-[0.3em] font-bold mb-4">{isArabic ? "المشاريع" : "Projects"}</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-accent mb-4">{t.projectsTitle}</h2>
            <p className="text-lg text-muted-foreground leading-relaxed">{t.projectsLede}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.projects.map((p) => (
              <div key={p.name} className="border border-border/60 rounded-xl p-6 bg-card hover:border-primary/40 transition-colors">
                <p className="text-xs uppercase tracking-wider text-primary font-bold mb-2">{p.type}</p>
                <h3 className="text-lg font-semibold text-accent mb-3">{p.name}</h3>
                <div className="flex items-center justify-between text-sm text-muted-foreground border-t border-border/50 pt-3">
                  <span>{p.client}</span>
                  <span>{p.period}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 bg-gradient-to-br from-primary to-primary/80">
        <div className="container mx-auto px-6 lg:px-10 text-center">
          <Building2 className="w-12 h-12 text-secondary mx-auto mb-6" />
          <h2 className="text-3xl lg:text-4xl font-bold text-primary-foreground mb-4">{t.ctaTitle}</h2>
          <p className="text-primary-foreground/85 text-lg mb-8 max-w-xl mx-auto">{t.ctaBody}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-secondary text-accent hover:bg-secondary/90"><Link to="/#contact">{t.ctaPrimary}</Link></Button>
            <Button asChild size="lg" variant="outline" className="bg-white/10 text-white border-white/40 hover:bg-white/20"><Link to="/#services">{t.ctaSecondary}</Link></Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contracting;